/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from 'react';
import { supabase } from './supabase';

// Gantt-style daily schedule. Used by Home (drill-in drawer) and Schedule's day view.
// Rows are employees sorted by their first start time; bars are colored by role with a
// 1-letter glyph. Click a bar to mark called-out (or revert).

const ROLE_SOLID = {
  'Cook': '#EA580C', 'Prep Cook': '#DC2626', 'Dishwasher': '#64748B',
  'Server': '#2563EB', 'Bartender': '#D97706', 'Host': '#9333EA',
  'Busser': '#0D9488', 'Manager': '#4B5563',
};
const ROLE_GLYPH = {
  'Cook': 'C', 'Prep Cook': 'P', 'Dishwasher': 'D',
  'Server': 'S', 'Bartender': 'B', 'Host': 'H',
  'Busser': 'U', 'Manager': 'M',
};

function parseHM(t) {
  if (!t) return null;
  const [h, m] = t.split(':').map(Number);
  return h + (m || 0) / 60;
}
function fmt12(t) {
  if (!t) return '';
  const [h, m] = t.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 || 12;
  return m && m !== 0 ? `${h12}:${String(m).padStart(2, '0')} ${ampm}` : `${h12} ${ampm}`;
}
function colorFor(role) { return ROLE_SOLID[role] || '#6B7280'; }
function glyphFor(role) { return ROLE_GLYPH[role] || (role || '?').slice(0, 1).toUpperCase(); }
// Local-time YYYY-MM-DD (vs. toISOString which is UTC and shifts past midnight in some zones).
function fmtLocalDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

// FOH/BOH bucket mapping for the optional role filter (matches Dashboard.js).
const ROLE_TYPE = {
  'Cook': 'BOH', 'Prep Cook': 'BOH', 'Dishwasher': 'BOH',
  'Server': 'FOH', 'Bartender': 'FOH', 'Host': 'FOH', 'Busser': 'FOH', 'Manager': 'FOH',
};

export default function DayTimeline({
  companyId,
  date,
  hourStart = 7,
  hourEnd = 24,
  editable = false,
  filterType = 'All',
  onClose,
  onShiftsChanged,
}) {
  const [shifts, setShifts] = useState([]);
  const [empMap, setEmpMap] = useState({});
  const [loading, setLoading] = useState(true);
  // Hover tooltip — coordinates are viewport-pixel because the tooltip uses position:fixed.
  const [hover, setHover] = useState(null); // { shift, role, x, y } | null

  useEffect(() => {
    if (!companyId || !date) return;
    (async () => {
      setLoading(true);
      const [sRes, eRes] = await Promise.all([
        supabase.from('shifts').select('*').eq('company_id', companyId).eq('day', date).eq('is_active', true).order('start_time', { ascending: true }),
        supabase.from('employees').select('id, name, initials, color, role, phone').eq('company_id', companyId),
      ]);
      const m = {};
      (eRes.data || []).forEach(e => { m[String(e.id)] = e; });
      setEmpMap(m);
      setShifts(sRes.data || []);
      setLoading(false);
    })();
  }, [companyId, date]);

  // Group shifts per employee so a single person with two shifts gets one row with two bars.
  // Optionally filter by FOH/BOH using the bar's role (or the employee's primary role).
  const rows = useMemo(() => {
    const matchesFilter = (shift, emp) => {
      if (filterType === 'All') return true;
      const role = shift.role || (typeof emp?.role === 'string' ? emp.role.split(',')[0].trim() : '');
      const bucket = ROLE_TYPE[role];
      return bucket === filterType;
    };
    const byEmp = new Map();
    shifts.forEach(s => {
      if (!s.employee_id) return;
      const emp = empMap[String(s.employee_id)] || null;
      if (!matchesFilter(s, emp)) return;
      const k = String(s.employee_id);
      if (!byEmp.has(k)) byEmp.set(k, { employee_id: s.employee_id, emp, shifts: [] });
      byEmp.get(k).shifts.push(s);
    });
    const arr = Array.from(byEmp.values());
    arr.forEach(r => r.shifts.sort((a, b) => (a.start_time || '').localeCompare(b.start_time || '')));
    arr.sort((a, b) => {
      const aStart = parseHM(a.shifts[0]?.start_time) ?? 99;
      const bStart = parseHM(b.shifts[0]?.start_time) ?? 99;
      return aStart - bStart;
    });
    return arr;
  }, [shifts, empMap, filterType]);

  async function toggleCalledOut(shift) {
    const next = !shift.called_out;
    setShifts(prev => prev.map(s => s.id === shift.id ? { ...s, called_out: next } : s));
    await supabase.from('shifts').update({
      called_out: next,
      called_out_at: next ? new Date().toISOString() : null,
    }).eq('id', shift.id);
    if (onShiftsChanged) onShiftsChanged();
  }

  // Persist a drag-resize. Snaps to 15-min increments by the time we get here.
  async function persistResize(shift, newStart, newEnd) {
    if (newStart === shift.start_time && newEnd === shift.end_time) return;
    setShifts(prev => prev.map(s => s.id === shift.id ? { ...s, start_time: newStart, end_time: newEnd } : s));
    await supabase.from('shifts').update({ start_time: newStart, end_time: newEnd }).eq('id', shift.id);
    if (onShiftsChanged) onShiftsChanged();
  }

  const dateLabel = (() => {
    const d = new Date(date + 'T00:00:00');
    return d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
  })();

  // Drag-resize handler. Captures the track rect on mousedown, snaps each move to 15 min,
  // and only persists once on mouseup so we don't spam Supabase mid-drag.
  function startDrag(shift, edge, e, trackEl) {
    e.preventDefault();
    e.stopPropagation();
    const rect = trackEl.getBoundingClientRect();
    let newStart = shift.start_time;
    let newEnd = shift.end_time;
    const totalMins = (hourEnd - hourStart) * 60;
    function pctToTime(pct) {
      const minsFromStart = Math.max(0, Math.min(totalMins, Math.round((pct * totalMins) / 15) * 15));
      const absMin = minsFromStart + hourStart * 60;
      const hh = String(Math.floor(absMin / 60) % 24).padStart(2, '0');
      const mm = String(absMin % 60).padStart(2, '0');
      return `${hh}:${mm}`;
    }
    function onMove(ev) {
      const pct = (ev.clientX - rect.left) / rect.width;
      const t = pctToTime(pct);
      if (edge === 'left') newStart = t;
      else newEnd = t;
      // Light optimistic update so the bar follows the cursor.
      setShifts(prev => prev.map(s => s.id === shift.id ? { ...s, start_time: newStart, end_time: newEnd } : s));
    }
    function onUp() {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      persistResize(shift, newStart, newEnd);
    }
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }

  // Now-line: vertical dashed marker showing the current time. Only rendered when the
  // displayed date is today AND now falls within the visible hour range.
  const today = new Date();
  const isToday = fmtLocalDate(today) === date;
  const nowMins = today.getHours() * 60 + today.getMinutes();
  const nowInRange = isToday && nowMins >= hourStart * 60 && nowMins <= hourEnd * 60;
  const nowPct = nowInRange ? ((nowMins / 60 - hourStart) / (hourEnd - hourStart)) * 100 : null;

  // Hour grid + bar geometry. Times are stored as 24h "HH:MM" strings; midnight-spanning shifts
  // are clamped at hourEnd so we don't render off the right edge (rare for restaurants).
  const totalHours = hourEnd - hourStart;
  const hours = Array.from({ length: totalHours + 1 }, (_, i) => hourStart + i);
  const xPctFor = (t) => {
    const h = parseHM(t);
    if (h == null) return 0;
    const clamped = Math.max(hourStart, Math.min(hourEnd, h < hourStart ? h + 24 : h));
    return ((clamped - hourStart) / totalHours) * 100;
  };
  const barWidthPct = (start, end) => {
    const a = xPctFor(start);
    const b = xPctFor(end);
    return Math.max(0.5, b - a);
  };

  return (
    <div className="bg-white">
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
        <div>
          <div className="text-sm font-semibold text-gray-900">{dateLabel}</div>
          <div className="text-xs text-gray-400 mt-0.5">{rows.length} on the floor · click a bar to mark called-out</div>
        </div>
        {onClose && (
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-lg" aria-label="Close">&times;</button>
        )}
      </div>

      {loading ? (
        <div className="p-6 text-center text-sm text-gray-400">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="p-10 text-center text-sm text-gray-400">No shifts scheduled</div>
      ) : (
        <div className="overflow-x-auto">
          <div className="min-w-[720px]">
            {/* Hour ruler — denser top padding so the chart starts higher up. */}
            <div className="flex items-end pl-32 pr-2 pt-2 pb-0.5 border-b border-gray-100">
              <div className="flex-1 relative h-4">
                {hours.map((h) => {
                  const left = ((h - hourStart) / totalHours) * 100;
                  const label = (h % 12 === 0 ? 12 : h % 12) + (h < 12 || h === 24 ? 'A' : 'P');
                  const adj = h >= 24 ? (h - 24 < 12 ? 'A' : 'P') : null;
                  return (
                    <div key={h} className="absolute -translate-x-1/2 text-[10px] text-gray-400" style={{ left: `${left}%` }}>
                      {adj || label}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Rows — compact: shorter tracks, less vertical padding so more shifts fit on one screen. */}
            <div>
              {rows.map((row) => {
                const e = row.emp || {};
                const name = e.name || `Employee #${row.employee_id}`;
                return (
                  <div key={row.employee_id} className="flex items-center" style={{ minHeight: 26 }}>
                    <div className="w-32 pr-2 text-right pl-3">
                      <div className="text-[11px] font-medium text-gray-900 truncate" title={name}>{name}</div>
                    </div>
                    <div className="flex-1 relative h-6 pr-2 dt-track">
                      {/* gridlines */}
                      {hours.map((h) => {
                        const left = ((h - hourStart) / totalHours) * 100;
                        return <div key={h} className="absolute top-0 bottom-0 w-px bg-gray-100" style={{ left: `${left}%` }} />;
                      })}
                      {/* now-line */}
                      {nowPct != null && (
                        <div className="absolute top-0 bottom-0 pointer-events-none" style={{ left: `${nowPct}%`, borderLeft: '1.5px dashed #ef4444', zIndex: 2 }} />
                      )}
                      {row.shifts.map((s) => {
                        const role = s.role || (typeof e.role === 'string' ? e.role.split(',')[0].trim() : '');
                        const bg = colorFor(role);
                        const left = xPctFor(s.start_time);
                        const width = barWidthPct(s.start_time, s.end_time);
                        const dimmed = !!s.called_out;
                        return (
                          <div
                            key={s.id}
                            className="absolute top-0.5 bottom-0.5 rounded text-white text-[10px] font-medium flex items-center overflow-hidden hover:brightness-110 transition-all"
                            style={{
                              left: `${left}%`,
                              width: `${width}%`,
                              background: bg,
                              opacity: dimmed ? 0.35 : 1,
                              boxShadow: dimmed ? 'none' : '0 1px 2px rgba(0,0,0,0.12)',
                            }}
                            onMouseEnter={(ev) => setHover({ shift: s, role, x: ev.clientX, y: ev.clientY })}
                            onMouseMove={(ev) => setHover((h) => h && h.shift.id === s.id ? { ...h, x: ev.clientX, y: ev.clientY } : h)}
                            onMouseLeave={() => setHover(null)}
                          >
                            {/* Left grab handle (edit mode only) */}
                            {editable && (
                              <div
                                onMouseDown={(ev) => startDrag(s, 'left', ev, ev.currentTarget.closest('.dt-track'))}
                                className="absolute left-0 top-0 bottom-0 w-2 cursor-ew-resize hover:bg-white/20"
                                style={{ zIndex: 3 }}
                              />
                            )}
                            <button
                              onClick={() => toggleCalledOut(s)}
                              className="flex-1 h-full flex items-center gap-1 px-1.5 overflow-hidden text-white"
                              style={{
                                textDecoration: dimmed ? 'line-through' : 'none',
                                cursor: 'pointer',
                              }}
                            >
                              <span className="inline-flex items-center justify-center w-3.5 h-3.5 rounded text-[9px] font-bold flex-shrink-0" style={{ background: 'rgba(255,255,255,0.25)' }}>
                                {glyphFor(role)}
                              </span>
                              <span className="truncate">{fmt12(s.start_time)}–{fmt12(s.end_time)}</span>
                            </button>
                            {/* Right grab handle (edit mode only) */}
                            {editable && (
                              <div
                                onMouseDown={(ev) => startDrag(s, 'right', ev, ev.currentTarget.closest('.dt-track'))}
                                className="absolute right-0 top-0 bottom-0 w-2 cursor-ew-resize hover:bg-white/20"
                                style={{ zIndex: 3 }}
                              />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Hover tooltip — fixed-position card pinned to the cursor with a small offset so the
         pointer doesn't sit on top of it (which would cause flicker on mousemove). */}
      {hover && (() => {
        const e = empMap[String(hover.shift.employee_id)] || {};
        const phone = e.phone || '';
        const offsetX = 14, offsetY = 14;
        const style = {
          position: 'fixed',
          left: hover.x + offsetX,
          top: hover.y + offsetY,
          zIndex: 60,
          pointerEvents: 'none',
          minWidth: 200,
        };
        return (
          <div style={style} className="bg-white rounded-lg border border-gray-200 px-3 py-2 text-xs" data-shadow="hover">
            <div className="font-semibold text-gray-900">{e.name || `Employee #${hover.shift.employee_id}`}</div>
            <div className="text-gray-600 mt-0.5">{fmt12(hover.shift.start_time)}–{fmt12(hover.shift.end_time)}</div>
            <div className="flex items-center gap-1.5 text-gray-500 mt-1">
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
              <span>{hover.role || '—'}</span>
            </div>
            {phone && (
              <div className="flex items-center gap-1.5 text-gray-500 mt-0.5">
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.13.96.37 1.9.72 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.91.35 1.85.59 2.81.72A2 2 0 0122 16.92z"/></svg>
                <a href={`tel:${phone}`} className="hover:underline" style={{ pointerEvents: 'auto' }}>{phone}</a>
              </div>
            )}
            {hover.shift.called_out && (
              <div className="text-red-600 font-medium mt-1">Called out</div>
            )}
          </div>
        );
      })()}
      <style>{`[data-shadow="hover"] { box-shadow: 0 8px 24px rgba(0,0,0,0.12); }`}</style>
    </div>
  );
}
